<template>
  <div class="addon-selector">
    <div class="field">
      <label class="label">Select Additional Services</label>
      <div class="control">
        <div v-if="isLoading" class="has-text-centered">
          <b-loading :is-full-page="false" v-model="isLoading"></b-loading>
          <p>Loading addon options...</p>
        </div>

        <div v-else-if="addonList.length" class="addon-list">
          <div
            v-for="addon in addonList"
            :key="addon.addon_id"
            class="addon-item"
          >
            <b-checkbox
              v-model="selectedAddons"
              :native-value="addon"
              @input="updateSelectedAddons"
            >
              <div class="addon-content">
                <div class="addon-header">
                  <span class="addon-title">{{ addon.addon_name_en }}</span>
                  <span class="addon-price tag is-primary">
                    {{ formatPrice(addon.addon_price) }} ฿
                  </span>
                </div>
                <p class="addon-description">{{ addon.addon_name_th }}</p>
              </div>
            </b-checkbox>
          </div>

          <div v-if="selectedAddons.length" class="selected-summary">
            <div class="box">
              <h6 class="title is-6">Selected Services Summary</h6>
              <div class="tags">
                <span
                  v-for="addon in selectedAddons"
                  :key="addon.addon_id"
                  class="tag is-info"
                >
                  {{ addon.addon_name_en }} - {{ formatPrice(addon.addon_price) }} ฿
                  <button
                    class="delete is-small"
                    @click="removeAddon(addon)"
                  ></button>
                </span>
              </div>
              <div class="total-price">
                <strong>Total Addon Price: {{ totalAddonPrice }} ฿</strong>
              </div>
            </div>
          </div>
        </div>

        <div v-else class="has-text-centered">
          <p class="has-text-grey">No additional services available for this location</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "WeServeAddonSelector",
  props: {
    value: {
      type: Array,
      default: () => []
    },
    location: {
      type: Object,
      required: true
    },
    vehicleId: {
      type: Number,
      default: null
    }
  },
  data() {
    return {
      isLoading: false,
      selectedAddons: [...this.value]
    };
  },
  computed: {
    addonList() {
      const allAddons = this.$store.getters["weserve/addonList"];

      // Filter addons by vehicle if vehicleId is provided
      if (this.vehicleId && Array.isArray(allAddons)) {
        return allAddons.filter(addon =>
          !addon.addon_vehicle_id || addon.addon_vehicle_id === this.vehicleId
        );
      }

      return allAddons || [];
    },
    totalAddonPrice() {
      return this.selectedAddons
        .reduce((total, addon) => total + parseFloat(addon.addon_price || 0), 0)
        .toFixed(2);
    }
  },
  watch: {
    value(newVal) {
      this.selectedAddons = [...newVal];
    },
    location: {
      immediate: true,
      handler() {
        this.loadAddons();
      }
    },
    vehicleId() {
      // Clear selected addons that are not compatible with new vehicle
      this.selectedAddons = this.selectedAddons.filter(addon =>
        !addon.addon_vehicle_id || addon.addon_vehicle_id === this.vehicleId
      );
      this.updateSelectedAddons();
    }
  },
  methods: {
    async loadAddons() {
      if (!this.location || !this.location.lat || !this.location.lng) return;

      this.isLoading = true;
      try {
        await this.$store.dispatch("weserve/getAddOnList", {
          lat: this.location.lat,
          lng: this.location.lng
        });
      } catch (error) {
        console.error("Failed to load addons:", error);
        this.$buefy.notification.open({
          message: "Failed to load addon options",
          type: "is-danger",
          duration: 3000
        });
      } finally {
        this.isLoading = false;
      }
    },
    updateSelectedAddons() {
      // Convert to the format expected by WeServe API
      const formattedAddons = this.selectedAddons.map(addon => ({
        addon_id: addon.addon_id
      }));

      this.$emit("input", this.selectedAddons);
      this.$emit("addons-selected", {
        addons: this.selectedAddons,
        formatted: formattedAddons,
        totalPrice: parseFloat(this.totalAddonPrice)
      });
    },
    removeAddon(addonToRemove) {
      this.selectedAddons = this.selectedAddons.filter(
        addon => addon.addon_id !== addonToRemove.addon_id
      );
      this.updateSelectedAddons();
    },
    formatPrice(price) {
      return parseFloat(price || 0).toFixed(2);
    }
  }
};
</script>

<style scoped>
.addon-selector {
  position: relative;
  min-height: 150px;
}

.addon-list {
}

.addon-item {
  padding: 1rem;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  margin-bottom: 0.5rem;
  transition: all 0.3s ease;
}

.addon-item:hover {
  border-color: #3273dc;
  background-color: #f8f9fa;
}

.addon-content {
  margin-left: 0.5rem;
}

.addon-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.25rem;
}

.addon-title {
  font-weight: 600;
  font-size: 1rem;
}

.addon-description {
  color: #666;
  font-size: 0.875rem;
  margin: 0;
}

.addon-price {
  margin-left: 0.5rem;
}

.selected-summary {
  margin-top: 1rem;
}

.total-price {
  margin-top: 0.5rem;
  text-align: right;
  font-size: 1.1rem;
}

.tags .tag {
  margin-right: 0.5rem;
  margin-bottom: 0.25rem;
}
</style>